import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPresencePing, onAuthContextReady } from './userEventTracker';
import { normalizeRoute } from './analyticsRouteNormalizer';
import { getActiveMsSince } from './idleTracker';
import { getActivities, subscribeActivities } from './activityTracker';

export const PRESENCE_INTERVAL_MS = 30000;
const ACTIVITY_DEBOUNCE_MS = 500;

let currentPage = '/';
let currentFullPath = '/';

/** Normalized route of the page the user is on; read by the fetch wrapper. */
export function getCurrentPage() {
  return currentPage;
}

export function _setCurrentPageForTests(page, fullPath) {
  currentPage = page;
  currentFullPath = fullPath || page;
}

function sendPing(reason) {
  const now = Date.now();
  let visible = true;
  try { visible = typeof document === 'undefined' || document.visibilityState !== 'hidden'; } catch (_) {}
  return trackPresencePing({
    page: currentPage,
    fullPath: currentFullPath,
    activities: getActivities(),
    active: getActiveMsSince(now - PRESENCE_INTERVAL_MS, now) > 0,
    visible,
    reason,
  });
}

/**
 * Mount once in App.js next to usePageTimeTracking(). Sends a presence_ping on
 * every route change, every 30s, on tab visibility changes, when the activity
 * set changes (debounced), and as soon as the auth context becomes ready.
 */
export function usePresencePinger() {
  const location = useLocation();
  const debounceRef = useRef(null);

  useEffect(() => {
    currentPage = normalizeRoute(location.pathname);
    currentFullPath = location.pathname + location.search;
    sendPing('route');
  }, [location.pathname, location.search]);

  useEffect(() => {
    const timer = setInterval(() => sendPing('interval'), PRESENCE_INTERVAL_MS);
    const onVisibility = () => sendPing('visibility');
    document.addEventListener('visibilitychange', onVisibility);
    const unsubscribeActivities = subscribeActivities(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        sendPing('activity');
      }, ACTIVITY_DEBOUNCE_MS);
    });
    const unsubscribeAuth = onAuthContextReady(() => sendPing('auth'));
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      unsubscribeActivities();
      unsubscribeAuth();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
}
